/**
 * Unified PDP inventory gate — one getProducts batch when blocked + notify share the
 * same selection IDs (e.g. Complex PDP change set).
 *
 * Family A (no visible options): {@link getCoverInventoryDecision} owns ATC /
 * Notify Me from the selected cover’s `inventoryProductId`, including silent MTO
 * when the linked FG is OOS + isBackInStock=false (AC4).
 *
 * Family B (visible options): this cover path is skipped — leftover
 * `inventoryProductId` on covers must not flip CTA to Notify Me (DE-819).
 */

import {useMemo} from 'react';

import {PDP_INVENTORY_WAIT_MS, useBoundedPending} from '@amblique/king-living/app/hooks/use-bounded-pending';
import {useCommittedCoverVariantProduct} from '@amblique/king-living/app/hooks/use-committed-cover-variant-product';
import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {
  buildPdpInventoryProductIds,
  usePdpSelectionInventoryProducts,
} from '@amblique/king-living/app/hooks/use-pdp-selection-inventory-products';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {
  getCoverInventoryDecision,
  getInventory,
  getInventoryProductIdFromVariant,
  INVENTORY_STATUS,
  isInventoryFamilyA,
  shouldShowNotifyMeForProduct,
} from '@amblique/king-living/app/utils/inventory-utils';

function isProductOutOfStockForWarehouse(product, variant, shopperInventoryIds) {
  return getInventory(product, variant, shopperInventoryIds).status === INVENTORY_STATUS.OUT_OF_STOCK;
}

/**
 * Cover→FG decision for Family A (no visible options) when a cover is selected.
 * Passes live FG from the warehouse getProducts batch when present.
 *
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} selections
 * @param {Record<string, Object>} [productsById]
 * @param {string|null|undefined} shopperInventoryIds
 * @returns {ReturnType<typeof getCoverInventoryDecision>|null}
 */
function resolveLinkedCoverDecision(product, selections, productsById, shopperInventoryIds) {
  if (!product || !selections || !isInventoryFamilyA(product)) {
    return null;
  }

  const coverId = selections.cover?.productId ?? selections.cover?.variantId;

  if (!coverId) {
    return null;
  }

  const coverEntry = getSelectedVariantProduct(product, coverId);
  const fgId = getInventoryProductIdFromVariant(coverEntry);
  const liveFg = fgId && productsById ? productsById[fgId] ?? null : null;

  return getCoverInventoryDecision(product, coverEntry, shopperInventoryIds, liveFg);
}

/**
 * @param {Object} options
 * @param {Object|null|undefined} options.product
 * @param {Object|null|undefined} options.variant
 * @param {Object|null|undefined} options.blockedSelections - Live selections for ATC block (draft or committed)
 * @param {Object|null|undefined} options.committedSelection
 * @param {boolean} [options.blockedEnabled=true]
 * @param {boolean} [options.notifyEnabled=true]
 * @param {boolean} [options.isSimplePdpFlow=false]
 * @param {boolean} [options.isSimpleMasterWithCovers=false]
 * @returns {{
 *   isAddToCartBlocked: boolean,
 *   isInventoryPending: boolean, // Bounded — false after {@link PDP_INVENTORY_WAIT_MS} even if the fetch is still in flight
 *   linkedFinishedGoodProduct: Object|null,
 *   notifyMeMode: boolean,
 *   primaryVariantProduct: Object|null,
 * }}
 */
export function usePdpInventoryGate({
  product,
  variant,
  blockedSelections,
  committedSelection,
  blockedEnabled = true,
  notifyEnabled = true,
  isSimplePdpFlow = false,
  isSimpleMasterWithCovers = false,
}) {
  const {shopperInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();
  const notifySelections = committedSelection?.selections;
  const coverVariant = useCommittedCoverVariantProduct(product);

  const blockedProductIds = useMemo(
    () => buildPdpInventoryProductIds(blockedSelections, {product, variant}),
    [blockedSelections, product, variant]
  );

  const notifyProductIds = useMemo(
    () => (notifyEnabled && notifySelections ?
      buildPdpInventoryProductIds(notifySelections, {product, variant}) :
      []),
    [notifyEnabled, notifySelections, product, variant]
  );

  const primaryProductId = useMemo(() => {
    const coverId = blockedSelections?.cover?.productId ?? blockedSelections?.cover?.variantId;

    return coverId ?? variant?.productId ?? product?.id ?? product?.productId ?? null;
  }, [blockedSelections, variant, product]);

  const sharedProductIds = useMemo(() => {
    if (blockedProductIds.length === 0 || notifyProductIds.length === 0) {
      return [];
    }

    return blockedProductIds.join(',') === notifyProductIds.join(',') ? blockedProductIds : [];
  }, [blockedProductIds, notifyProductIds]);

  const needsSharedFetch = sharedProductIds.length > 0;
  const needsBlockedOnlyFetch =
    blockedEnabled && blockedProductIds.length > 0 && !needsSharedFetch;
  const needsNotifyOnlyFetch =
    notifyEnabled && notifyProductIds.length > 0 && !needsSharedFetch;

  const sharedInventory = usePdpSelectionInventoryProducts({
    enabled: needsSharedFetch && (blockedEnabled || notifyEnabled),
    productIds: sharedProductIds,
  });

  const blockedOnlyInventory = usePdpSelectionInventoryProducts({
    enabled: needsBlockedOnlyFetch,
    productIds: blockedProductIds,
  });

  const notifyOnlyInventory = usePdpSelectionInventoryProducts({
    enabled: needsNotifyOnlyFetch,
    productIds: notifyProductIds,
  });

  const blockedInventory = needsSharedFetch ? sharedInventory : blockedOnlyInventory;
  const notifyInventory = needsSharedFetch ? sharedInventory : notifyOnlyInventory;

  const isInventoryPendingRaw =
    isInventoryIdsResolving ||
    (blockedEnabled && blockedInventory.isPending) ||
    (notifyEnabled && notifyInventory.isPending);

  const isInventoryFetchFailed =
    (blockedEnabled && blockedInventory.isError) ||
    (notifyEnabled && notifyInventory.isError);

  // Same timeout the CTA spinner uses. After it expires — or when getProducts
  // settles as an error with no payload — stop treating missing batch rows as
  // OOS and evaluate ATC / Notify Me from the site-default product flags.
  const isInventoryPending = useBoundedPending(isInventoryPendingRaw, PDP_INVENTORY_WAIT_MS);
  const useInventoryFallback =
    isInventoryFetchFailed || (isInventoryPendingRaw && !isInventoryPending);

  const primaryVariantProduct = useMemo(
    () => (primaryProductId ? blockedInventory.productsById[primaryProductId] ?? null : null),
    [primaryProductId, blockedInventory.productsById]
  );

  // Linked FG from the same getProducts batch (when cover has inventoryProductId).
  // Family A only — leftover links on Family B must not drive price / deposit / ATC.
  const linkedFinishedGoodProduct = useMemo(() => {
    if (!isInventoryFamilyA(product)) return null;

    const selections = blockedSelections || notifySelections;
    const coverId = selections?.cover?.productId ?? selections?.cover?.variantId;

    if (!coverId || !product) return null;

    const coverEntry = getSelectedVariantProduct(product, coverId);
    const fgId = getInventoryProductIdFromVariant(coverEntry);

    if (!fgId) return null;

    return (
      blockedInventory.productsById[fgId] ??
      notifyInventory.productsById[fgId] ??
      null
    );
  }, [
    blockedSelections,
    notifySelections,
    product,
    blockedInventory.productsById,
    notifyInventory.productsById,
  ]);

  const isAddToCartBlocked = useMemo(() => {
    if (!blockedEnabled || !product) {
      return false;
    }

    if (isInventoryIdsResolving && !useInventoryFallback) {
      return true;
    }

    // Family A: cover→FG decision owns ATC (silent MTO keeps ATC enabled).
    const coverDecision = resolveLinkedCoverDecision(
      product,
      blockedSelections,
      blockedInventory.productsById,
      shopperInventoryIds
    );

    // linked-pending means the FG batch is still in flight. After the wait
    // expires, fall through to site-default flags instead of leaving ATC
    // disabled with no Notify Me.
    if (coverDecision && !(useInventoryFallback && coverDecision.mode === 'linked-pending')) {
      return !coverDecision.canAddToCart;
    }

    const coverId = blockedSelections?.cover?.productId ?? blockedSelections?.cover?.variantId;
    const coverVariantProduct = coverId ? getSelectedVariantProduct(product, coverId) : null;
    const primaryVariant = coverVariantProduct ?? variant ?? undefined;

    if (isProductOutOfStockForWarehouse(product, primaryVariant, shopperInventoryIds)) {
      return true;
    }

    if (blockedProductIds.length > 0 && blockedInventory.isPending && !useInventoryFallback) {
      return true;
    }

    if (blockedProductIds.length > 0) {
      const {productsById} = blockedInventory;
      const allLoaded = blockedProductIds.every((id) => productsById[id]);

      if (!allLoaded && !useInventoryFallback) {
        return true;
      }

      const hasBlockedSelection = blockedProductIds.some((id) => {
        const loadedProduct = productsById[id];

        if (!loadedProduct) {
          return false;
        }

        return isProductOutOfStockForWarehouse(loadedProduct, undefined, shopperInventoryIds);
      });

      if (hasBlockedSelection) {
        return true;
      }
    }

    return false;
  }, [
    blockedEnabled,
    product,
    blockedSelections,
    variant,
    shopperInventoryIds,
    isInventoryIdsResolving,
    blockedProductIds,
    blockedInventory,
    useInventoryFallback,
  ]);

  const notifyMeMode = useMemo(() => {
    if (!notifyEnabled || !notifySelections) {
      return false;
    }

    if (notifyInventory.isPending && !useInventoryFallback) {
      return false;
    }

    const {productsById} = notifyInventory;

    // Family A: only Notify Me when cover decision says so (not silent MTO / not child ATS).
    const coverDecision = resolveLinkedCoverDecision(
      product,
      notifySelections,
      productsById,
      shopperInventoryIds
    );

    if (coverDecision && !(useInventoryFallback && coverDecision.mode === 'linked-pending')) {
      return coverDecision.showNotifyMe;
    }

    const coverId = notifySelections.cover?.productId ?? notifySelections.cover?.variantId;

    if (!isSimplePdpFlow && coverId) {
      const coverProduct = productsById[coverId];
      const coverSku = coverProduct ?? coverVariant;

      if (coverSku && shouldShowNotifyMeForProduct(coverSku, null, shopperInventoryIds)) {
        return true;
      }
    } else if (isSimplePdpFlow && !isSimpleMasterWithCovers) {
      if (shouldShowNotifyMeForProduct(product, null, shopperInventoryIds)) {
        return true;
      }
    }

    if (notifyProductIds.length === 0) {
      return false;
    }

    return notifyProductIds.some((id) => {
      const childProduct = productsById[id];

      if (!childProduct) {
        // Missing batch rows are only treated as Notify Me while we are still
        // waiting. After fallback, skip them so a slow fetch cannot flip ATC.
        return !useInventoryFallback;
      }

      return shouldShowNotifyMeForProduct(childProduct, null, shopperInventoryIds);
    });
  }, [
    notifyEnabled,
    notifySelections,
    notifyInventory,
    isSimplePdpFlow,
    isSimpleMasterWithCovers,
    product,
    coverVariant,
    shopperInventoryIds,
    notifyProductIds,
    useInventoryFallback,
  ]);

  return {
    isAddToCartBlocked,
    isInventoryPending,
    linkedFinishedGoodProduct,
    notifyMeMode,
    primaryVariantProduct,
  };
}

export default usePdpInventoryGate;
